import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../lib/supabase";
import { useNavigate, Link } from "react-router-dom";
import zxcvbn from "zxcvbn";
import logo from '../assets/logo.png';
import { FaEye, FaEyeSlash } from "react-icons/fa";
import Turnstile from 'react-turnstile';
import { toast } from 'sonner';

const RegisterForm = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordFeedback, setPasswordFeedback] = useState("");

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [verificationRequired, setVerificationRequired] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [turnstileKey, setTurnstileKey] = useState(0); // Used to force remount/reset
  const turnstileRef = useRef();

  const navigate = useNavigate();

  // Password strength check
  useEffect(() => {
    if (password) {
      const result = zxcvbn(password);
      setPasswordStrength(result.score);
      setPasswordFeedback(result.feedback.warning || "");
    } else {
      setPasswordStrength(0);
      setPasswordFeedback("");
    }
  }, [password]);

  // Handle Safari bfcache (back-forward cache) to prevent stale captcha tokens
  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        setCaptchaToken(null);
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case 0: return "bg-red-500";
      case 1: return "bg-red-400";
      case 2: return "bg-yellow-500";
      case 3: return "bg-emerald-400";
      case 4: return "bg-emerald-500";
      default: return "bg-gray-700";
    }
  };

  const getPasswordStrengthText = () => {
    switch (passwordStrength) {
      case 0: return "Very Weak";
      case 1: return "Weak";
      case 2: return "Fair";
      case 3: return "Good";
      case 4: return "Strong";
      default: return "";
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    setRegistrationSuccess(false);
    setVerificationRequired(false);

    if (!captchaToken) {
      setError("Please complete the CAPTCHA check.");
      setShowCaptcha(true);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Invalid email format.");
      return;
    }

    if (passwordStrength < 2) {
      setError("Please choose a stronger password. " + passwordFeedback);
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);



    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        captchaToken,
        data: {
          name, // Consolidated name field
        },
      },
    });

    if (error) {
      if (error.message.includes("already registered")) {
        setError(
          <span>
            Email already registered. <Link to="/login" className="underline font-bold hover:text-red-100">Sign in here</Link>.
          </span>
        );
        toast.error("Email already registered");
      } else {
        setError(error.message);
        toast.error(error.message);
      }
      setLoading(false);
      setTurnstileKey(prev => prev + 1);
      setCaptchaToken(null);
      return;
    }

    // Check if verification is required (user created but no session)
    if (data?.user && !data.session) {
      setVerificationRequired(true);
      toast.success("Account created! Please verify your email.");
      setLoading(false);
      setTurnstileKey(prev => prev + 1);
      setCaptchaToken(null);
      return;
    }

    setRegistrationSuccess(true);
    toast.success("Account created successfully!");
    setLoading(false);
    setTurnstileKey(prev => prev + 1); // Force remount
    setCaptchaToken(null);

    setTimeout(() => {
      navigate("/login");
    }, 2000);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-[#0a1220] p-4 transition-colors duration-300">
      <div className="w-full max-w-5xl bg-white dark:bg-[#162032] rounded-2xl shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-800 grid md:grid-cols-2 transition-colors duration-300">

        {/* LEFT COLUMN: Slick Promo (Reused style for consistency) */}
        <div className="hidden md:flex flex-col justify-center items-center text-center p-12 relative overflow-hidden bg-[#162032] bg-gradient-to-br from-purple-900/20 to-blue-900/20">
          <div className="absolute top-0 left-0 w-full h-full bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/20 rounded-full blur-[80px]"></div>
          <div className="absolute top-0 right-0 w-80 h-80 bg-blue-500/10 rounded-full blur-[100px]"></div>

          <div className="relative z-10 flex flex-col items-center">
            <Link to="/" className="mb-8 block">
              <img src={logo} alt="DataHelix Logo" className="h-24 w-auto drop-shadow-2xl hover:scale-105 transition-transform" />
            </Link>
            <h2 className="text-3xl font-bold text-white mb-6">
              Join the <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500">Revolution</span>
            </h2>
            <p className="text-gray-400 text-lg mb-8 leading-relaxed">
              Create your account to start solving real-world data problems. Join a community of analysts mastering SQL through practice.
            </p>

            <ul className="space-y-4 text-gray-400 font-mono text-sm">
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-blue-500"></div> Interactive SQL Editor
              </li>
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-purple-500"></div> Real Business Cases
              </li>
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-orange-500"></div> Career-Ready Portfolio
              </li>
            </ul>
          </div>
        </div>

        {/* RIGHT COLUMN: Form */}
        <div className="p-8 md:p-12 flex flex-col justify-center relative">

          {/* Logo - Mobile Only */}
          <div className="mb-6 md:hidden">
            <Link to="/" className="inline-block">
              <img src={logo} alt="DataHelix Logo" className="h-10 w-auto opacity-90 hover:opacity-100 transition-opacity" />
            </Link>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 md:hidden">Sign Up</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-8 md:hidden">Create your DataHelix account</p>

          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 hidden md:block">Create Account</h3>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-800 dark:text-red-200 p-3 rounded-lg mb-6 text-sm">
              {error}
            </div>
          )}

          {verificationRequired && (
            <div className="bg-blue-500/10 border border-blue-500/30 text-blue-800 dark:text-blue-200 p-4 rounded-lg mb-6 text-sm text-center">
              <p className="font-bold mb-1">Check your inbox!</p>
              <p className="opacity-80">We've sent a link to {email}. Click it to activate your account.</p>
            </div>
          )}

          {registrationSuccess && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-800 dark:text-emerald-200 p-3 rounded-lg mb-6 text-center">
              Success! Please check your email to confirm your account. Redirecting...
            </div>
          )}

          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <label className="block text-gray-400 text-xs font-mono mb-1 uppercase" htmlFor="name">Full Name</label>
              <input
                id="name"
                type="text"
                placeholder="Enter Your Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full p-3 bg-gray-50 dark:bg-[#0a1220] border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                required
              />
            </div>

            <div>
              <label className="block text-gray-400 text-xs font-mono mb-1 uppercase" htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                placeholder="Enter Your Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full p-3 bg-gray-50 dark:bg-[#0a1220] border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                required
              />
            </div>

            <div>
              <label className="block text-gray-400 text-xs font-mono mb-1 uppercase" htmlFor="password">Password</label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter Your Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full p-3 bg-gray-50 dark:bg-[#0a1220] border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
                >
                  {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                </button>
              </div>
            </div>

            {/* Password Strength Meter */}
            {password && (
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-gray-400">
                  <span>Strength</span>
                  <span className="font-medium text-white">{getPasswordStrengthText()}</span>
                </div>
                <div className="h-1 w-full bg-gray-800 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                    style={{ width: `${(passwordStrength + 1) * 20}%` }}
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-gray-400 text-xs font-mono mb-1 uppercase" htmlFor="confirmPassword">Confirm Password</label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm Your Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full p-3 bg-gray-50 dark:bg-[#0a1220] border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
                >
                  {showConfirmPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                </button>
              </div>
            </div>

            {/* Captcha: Always render. 
                "interaction-only" means it is invisible unless a challenge is required. */}
            <div className="flex justify-center pt-2">
              <Turnstile
                key={turnstileKey}
                ref={turnstileRef}
                sitekey={process.env.REACT_APP_TURNSTILE_SITE_KEY || "1x00000000000000000000AA"}
                onVerify={(token) => setCaptchaToken(token)}
                onError={() => setError("CAPTCHA failed. Please try again.")}
                onExpire={() => setCaptchaToken(null)}
                theme="dark"
                appearance={showCaptcha ? "always" : "interaction-only"}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3 rounded-lg text-white font-medium shadow-lg shadow-blue-500/20 ${loading
                ? "bg-blue-600/70 cursor-wait"
                : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 transform hover:-translate-y-0.5"
                } transition-all duration-200`}
            >
              {loading ? "Creating Account..." : "Create Account"}
            </button>
          </form>

          <div className="my-6 flex items-center">
            <div className="flex-1 border-t border-gray-800"></div>
            <span className="px-4 text-gray-500 text-xs uppercase font-mono">Or continue with</span>
            <div className="flex-1 border-t border-gray-800"></div>
          </div>

          <button
            onClick={async () => {
              setLoading(true);
              const { error } = await supabase.auth.signInWithOAuth({
                provider: "google",
                options: {
                  redirectTo: window.location.origin,
                },
              });
              if (error) {
                setError(error.message);
                toast.error(error.message);
                setLoading(false);
              }
            }}
            disabled={loading}
            className="w-full py-3 rounded-lg bg-white dark:bg-[#0a1220] border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white transition-all flex items-center justify-center gap-3 font-medium shadow-sm"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            Google
          </button>

          <p className="mt-8 text-center text-gray-400 text-sm">
            Already have an account?{" "}
            <a href="/login" className="text-blue-400 hover:text-blue-300 font-medium hover:underline">
              Sign In
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterForm;
